import React from 'react';
import { motion } from 'framer-motion';
import { CalendarIcon, BookOpenIcon, BriefcaseIcon, PenToolIcon, CompassIcon, EyeIcon, GlobeIcon, BookMarkedIcon, GraduationCapIcon, CheckCircle2Icon } from 'lucide-react';
import { Container } from '../components/ui/Container';
import { Button } from '../components/ui/Button';
import { PathCard } from '../components/ui/PathCard';
import { GeometricAccent } from '../components/ui/GeometricAccent';
import { PatternDecor } from '../components/ui/PatternDecor';
import { MosqueArchMask } from '../components/ui/MosqueArchMask';
import { Link } from 'react-router-dom';
export function Adults() {
  return <>
      {/* Hero */}
      <section className="relative overflow-hidden isolate" data-id="element-900" data-mp="2q5yeiqbninu2">
        <PatternDecor side="right" y="middle" size="lg" data-id="element-901" data-mp="2q8aq8lqtg1yo" />
        <GeometricAccent variant="arabesque" className="absolute -left-20 top-10 w-80 h-80" opacity={0.12} data-id="element-902" data-mp="jgthb654mrta" />
        <Container className="relative pt-8 md:pt-12 pb-16 md:pb-24" data-id="element-903" data-mp="ocqux4p09k2d">
          <div className="grid lg:grid-cols-12 gap-10 items-center" data-id="element-904" data-mp="2uq2r551kzfxd">
            <div className="lg:col-span-6" data-id="element-905" data-mp="2ptcbujxlood0">
              <p className="text-forest-700 font-semibold mb-4" data-id="element-906" data-mp="2psapvkf1qhgi">
                Applied learning for Muslim adults living in non-Muslim
                countries
              </p>
              <h1 className="font-display text-5xl md:text-7xl text-ink-900 leading-[1.05]" data-id="element-907" data-mp="2zjowsd9bujjo">
                Adult Programs
              </h1>
              <p className="mt-6 text-ink-700 max-w-xl leading-relaxed" data-id="element-908" data-mp="26ton813vsiah">
                Our adult programs support Muslims who want to live, work, and
                build families with confidence, clarity, and responsibility —
                while remaining true to their values and identity.
              </p>
              <p className="mt-4 text-ink-700 max-w-xl leading-relaxed" data-id="element-909" data-mp="2z0j5pwftip">
                These programs focus on the real-life application of Islamic
                principles, ethics, and professional skills. We complement — not
                replace — traditional religious education: our focus is on
                providing applied life skills and practical learning to support
                Muslims in contemporary societies.
              </p>
              <p className="mt-6 text-ink-900 font-semibold" data-id="element-910" data-mp="2yv31x0ps6gk6">
                Adult programs are designed for:
              </p>
              <ul className="mt-3 space-y-2 text-ink-700" data-id="element-911" data-mp="265mgwo4hq1xz">
                {['parents and young professionals', 'community members and leaders', 'educators and mentors', 'entrepreneurs and creatives'].map(c => <li key={c} className="flex items-center gap-3" data-id="element-912" data-mp="2jwsm90ugatss">
                    <CheckCircle2Icon size={16} className="text-forest-700 flex-shrink-0" strokeWidth={1.6} data-id="element-913" data-mp="2jz5b7xymm4fc" />
                    {c}
                  </li>)}
              </ul>
              <div className="mt-8 flex flex-wrap gap-3" data-id="element-914" data-mp="1jwp3vn0bysi1">
                <Link to="#programs" data-id="element-915" data-mp="1exm9qxlw26ou">
                  <Button variant="primary" data-id="element-916" data-mp="1eynll4aqax7m">Explore Programs</Button>
                </Link>
                <Link to="/en/contact" data-id="element-917" data-mp="1av7ok6uqyuue">
                  <Button variant="secondary" data-id="element-918" data-mp="1aydgruyoacuc">Contact Us</Button>
                </Link>
              </div>
            </div>
            <motion.div initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7
          }} className="lg:col-span-6 relative" data-id="element-919" data-mp="10ywbvfr8w2zf">
              <div className="relative w-full lg:w-[calc(100%+6rem)] lg:-ml-8 aspect-[200/148]" data-id="element-920" data-mp="n4v98ax69vts">
                <MosqueArchMask src="https://cdn.magicpatterns.com/uploads/4Uk2Rxk5Lbd1tQvC5K6BB6/Adults2.jpg" alt="Muslim professionals collaborating around laptops in a modern office" data-id="element-921" data-mp="i73lzu7h2xvx" />
              </div>
            </motion.div>
          </div>
        </Container>
      </section>

      {/* Explore */}
      <section id="programs" className="py-16 md:py-24 scroll-mt-24 bg-sage-50/60" data-id="element-922" data-mp="23m031829tseg">
        <Container data-id="element-923" data-mp="28j9s2mlr021r">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-924" data-mp="28iqknztq4w1z">
            Explore Adult Learning
          </h2>
          <div className="mt-10 grid md:grid-cols-2 gap-5" data-id="element-925" data-mp="1ym5f9ddslixm">
            <PathCard icon={CalendarIcon} tone="sage" title="Annual Programs" description="Year-long fellowships and certification paths for structured growth and consistent guidance." linkLabel="Explore" href="/en/adults/annual-programs" data-id="element-926" data-mp="1ynotyzjsvlxa" />
            <PathCard icon={BookOpenIcon} tone="sky" title="Core Courses" description="Applied short courses on identity, family life, integration, ethics, and Muslim daily-life essentials — practical and contextual." linkLabel="Browse" href="/en/adults/core-courses" data-id="element-927" data-mp="3jzgh4r8ci9ie" />
            <PathCard icon={BriefcaseIcon} tone="rose" title="Professional Development" description="Teacher certification and community leadership programs for educators, mentors, and community workers." linkLabel="View" href="/en/adults/professional-development" data-id="element-928" data-mp="1i390kn9frua8" />
            <PathCard icon={PenToolIcon} tone="sand" title="Creative Learning" description="Language, culture, and skills — Arabic, confidence languages, calligraphy, Islamic arts, and practical lifestyle skills." linkLabel="Discover" href="/en/adults/creative-learning" data-id="element-929" data-mp="21125tw7j8gas" />
          </div>
        </Container>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-24 bg-white/60" data-id="element-930" data-mp="rdwq298bdxr5">
        <Container data-id="element-931" data-mp="vegl979buran">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-932" data-mp="vdxdukhazlav">
            How our adult programs work
          </h2>
          <p className="mt-4 text-ink-700" data-id="element-933" data-mp="15blaxh2dt4w7">
            All adult programs are built around three pillars:
          </p>
          <div className="mt-10 grid md:grid-cols-3 gap-5" data-id="element-934" data-mp="1ab7k9ff3a0zq">
            {[{
            icon: CompassIcon,
            title: 'Applied Islamic values',
            text: 'Understanding how Islam guides everyday life.'
          }, {
            icon: EyeIcon,
            title: 'Integration & professional skills',
            text: 'Living confidently in non-Muslim societies.'
          }, {
            icon: GlobeIcon,
            title: 'Culture, language & creativity',
            text: 'Heritage as strength, not isolation.'
          }].map((p, i) => <motion.div key={p.title} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.08
          }} className={`text-center rounded-2xl border border-ink-900/5 p-6 shadow-soft ${['bg-sage-100', 'bg-[#F7EDE9]', 'bg-sand-100'][i]}`} data-id="element-935" data-mp="3dj7ylw89eetv" data-mpi={i}>
                <span className="w-12 h-12 inline-flex items-center justify-center rounded-full bg-white border border-forest-700/10 text-forest-700" data-id="element-936" data-mp="3mj4qe6py3tt5">
                  <p.icon size={20} strokeWidth={1.5} data-id="element-937" data-mp="j3funu3k1z84" />
                </span>
                <h3 className="mt-4 font-display text-lg text-ink-900" data-id="element-938" data-mp="j1cuwiclorq7">
                  {p.title}
                </h3>
                <p className="mt-2 text-ink-700 text-sm" data-id="element-939" data-mp="3sbgzzdhzo1km">{p.text}</p>
              </motion.div>)}
          </div>
          <p className="mt-10 text-center text-ink-500 text-sm" data-id="element-940" data-mp="3el3tlfkqwuxe">
            Our programs are delivered online through live, interactive sessions in focused groups and dynamic, guided workshops.
          </p>
        </Container>
      </section>

      {/* Featured annual programs */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-[#F7EDE9]/60" data-id="element-941" data-mp="f5pa2xcqqmzo">
        <PatternDecor side="left" y="top" size="lg" data-id="element-942" data-mp="15o81ekqp9im" />
        <Container data-id="element-943" data-mp="1564rxzntv17">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-944" data-mp="17sart6wk2hb">
            Featured Annual Programs
          </h2>
          <p className="mt-4 text-ink-700" data-id="element-945" data-mp="2lvdmhqhj6tas">
            Annual journeys designed for applied learning, reflection, and
            real-life growth.
          </p>
          <div className="mt-10 grid md:grid-cols-3 gap-5" data-id="element-946" data-mp="2qsdkde488pqt">
            {[{
            icon: CompassIcon,
            title: 'Muslim Life Fellowship',
            text: 'A year-long applied learning journey for Muslim adults living in non-Muslim countries — grounded in Islamic values and adapted to contemporary realities.',
            bg: 'bg-sage-100',
            to: '/en/adults/annual-programs#muslim-life-fellowship'
          }, {
            icon: BookMarkedIcon,
            title: 'Professional Muslim Leadership Track',
            text: 'For professionals and leaders who want to lead ethically, communicate effectively, and engage confidently in public and professional spaces.',
            bg: 'bg-[#EDF2F7]',
            to: '/en/adults/annual-programs#professional-leadership'
          }, {
            icon: GraduationCapIcon,
            title: 'Educator & Mentor Path',
            text: 'Certification for educators and mentors supporting Muslim children and families — child development, safeguarding, and ethical teaching.',
            bg: 'bg-[#F7EDE9]',
            to: '/en/adults/annual-programs#educator-mentor'
          }].map(p => <motion.div key={p.title} whileHover={{
            y: -2
          }} transition={{
            duration: 0.2,
            ease: [0.22, 1, 0.36, 1]
          }} className="rounded-2xl" data-id="element-947" data-mp="3tnllq6nzcltk">
                <Link to={p.to} className={`group block h-full rounded-2xl border border-ink-900/5 ${p.bg} p-6 shadow-soft transition-shadow hover:shadow-card`} data-id="element-948" data-mp="3eyfnakwyuzmq">
                  <span className="w-11 h-11 inline-flex items-center justify-center rounded-full bg-white border border-forest-700/10 text-forest-700" data-id="element-949" data-mp="35yak6myt2ned">
                    <p.icon size={18} strokeWidth={1.6} data-id="element-950" data-mp="311abs5ehh8k3" />
                  </span>
                  <h3 className="mt-5 font-display text-lg text-ink-900" data-id="element-951" data-mp="1ggm0octlumf2">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-ink-700 text-sm leading-relaxed" data-id="element-952" data-mp="1phk711rin60t">
                    {p.text}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-forest-700 font-medium text-sm" data-id="element-953" data-mp="1uh6gd048424c">
                    Learn more
                    <span aria-hidden="true" className="transition-transform group-hover:translate-x-0.5" data-id="element-954" data-mp="xs8opqrrskbe">
                      →
                    </span>
                  </span>
                </Link>
              </motion.div>)}
          </div>
        </Container>
      </section>

      {/* Learning formats */}
      <section className="py-16 md:py-24 bg-sand-50/60" data-id="element-955" data-mp="e1vhq3qshuwu">
        <Container data-id="element-956" data-mp="j0zq4s5w3om4">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-957" data-mp="j0g9derz769o">
            Learning Formats
          </h2>
          <div className="mt-10 grid md:grid-cols-3 gap-5" data-id="element-958" data-mp="1makjcshlnauo">
            {[{
            title: 'Core Courses',
            points: ['identity, values, family, integration', '8 weeks', 'group-based format', 'the foundation of the adult direction'],
            bg: 'bg-sage-100'
          }, {
            title: 'Enrichment Courses',
            points: ['languages, calligraphy, culture', '4–8 weeks', 'optional / add-on'],
            bg: 'bg-[#EDF2F7]'
          }, {
            title: 'Workshops & Intensives',
            points: ['weekend format', '1–2 days', 'practical topics (finance, marriage, business, life skills)'],
            bg: 'bg-sand-100'
          }].map(f => <div key={f.title} className={`rounded-2xl border border-ink-900/5 ${f.bg} p-6 shadow-soft`} data-id="element-959" data-mp="2vcnntd1ld6f8">
                <h3 className="font-display text-lg text-ink-900" data-id="element-960" data-mp="2hez0lyk1xn70">{f.title}</h3>
                <ul className="mt-4 space-y-2 text-ink-700 text-sm" data-id="element-961" data-mp="2md0iig5qrggw">
                  {f.points.map(p => <li key={p} className="flex items-center gap-2" data-id="element-962" data-mp="3a2bnk9u4jx0u">
                      <span className="w-1.5 h-1.5 rounded-full bg-forest-700 flex-shrink-0" data-id="element-963" data-mp="3f165zao6jjkf" />
                      {p}
                    </li>)}
                </ul>
              </div>)}
          </div>
        </Container>
      </section>

      {/* Notes */}
      <section className="py-16 md:py-24 bg-white/60" data-id="element-964" data-mp="3t05socs4tody">
        <Container data-id="element-965" data-mp="3syky4n5ti5g3">
          <div className="space-y-5 text-ink-700 leading-relaxed" data-id="element-966" data-mp="1m03edasx51u">
            <p data-id="element-967" data-mp="1nb76d48ycsy">
              <strong className="text-ink-900" data-id="element-968" data-mp="1ntblx12bxby">Adult programs</strong> provide
              applied education in values, ethics, cultural literacy, and life
              skills, supporting confident and responsible participation of
              Muslim adults in non-Muslim societies.
            </p>
            <p data-id="element-969" data-mp="206pysco9j7j2">
              <strong className="text-ink-900" data-id="element-970" data-mp="1v61y0za8fcbn">Islamic content</strong> is
              delivered in a contextual, practical, and respectful of
              traditional Islamic scholarship manner, focused on ethical
              development, social responsibility, and positive integration.
            </p>
            <p data-id="element-971" data-mp="2j37nsmbq8tbm">
              <strong className="text-ink-900" data-id="element-972" data-mp="2j2ze13awi44n">
                Business and finance-related courses
              </strong>{' '}
              focus on ethical decision-making, transparency, and responsible
              economic participation, without providing regulated financial or
              legal advice.
            </p>
            <p data-id="element-973" data-mp="36rqtqv7b0u0j">
              <strong className="text-ink-900" data-id="element-974" data-mp="36r8sfk5sbplr">
                Cultural and creative programs
              </strong>{' '}
              promote heritage appreciation, self-expression, and community
              skills, contributing to social inclusion and well-being.
            </p>
          </div>
        </Container>
      </section>

      {/* Start journey */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-[#EDF2F7]/60" data-id="element-975" data-mp="3t2iiql4rcl8f">
        <PatternDecor side="right" y="middle" size="md" data-id="element-976" data-mp="wwvca2vl0s7" />
        <Container data-id="element-977" data-mp="5x1a9vo4xngb">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8" data-id="element-978" data-mp="5wj4a7bo7uv7">
            <div className="max-w-xl" data-id="element-979" data-mp="an7xbr300zwg">
              <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-980" data-mp="ao9m0ycxtkwn">
                Start your learning journey
              </h2>
              <p className="mt-4 text-ink-700" data-id="element-981" data-mp="km793ij29bno">
                Choose an annual program or begin with a focused course —
                practical, applied, and designed for modern life.
              </p>
            </div>
            <div className="flex flex-wrap gap-3" data-id="element-982" data-mp="2lp83cu34tdbo">
              <Link to="#programs" data-id="element-983" data-mp="2llskrha91m5i">
                <Button variant="primary" data-id="element-984" data-mp="2gpv6dgijqpvd">Browse Programs</Button>
              </Link>
              <Link to="/en/contact" data-id="element-985" data-mp="2ug8gnpiwugnv">
                <Button variant="secondary" data-id="element-986" data-mp="2uh9yqdycg3f3">Request Info</Button>
              </Link>
            </div>
          </div>
        </Container>
      </section>
    </>;
}